<template>
  <!-- The agent sends the user here to approve the connection. Nothing of the
       app shows around it: one card, the company to connect, and a button. -->
  <div class="authorize-agent">
    <div class="authorize-card">
      <template v-if="fatalError">
        <p class="authorize-error mb-0">{{ fatalError }}</p>
      </template>

      <template v-else>
        <!-- The product name is the product name in every language, so only the
             line above it is translated. -->
        <h2 class="authorize-title">
          {{ $t("authorizeAgent.title") }}<br />Digital Permit Book
        </h2>
        <p class="authorize-note mb-0">{{ $t("authorizeAgent.subtitle") }}</p>

        <div class="text-left mt-4">
          <label class="state">{{ $t("authorizeAgent.companyLabel") }}</label>
          <b-form-select v-model="companyId" class="f-14" :options="companyOptions"></b-form-select>
          <p v-if="error" class="authorize-error mt-2 mb-0">{{ error }}</p>
        </div>

        <div class="d-flex align-items-center justify-content-end mt-4">
          <b-button variant="secondary" class="mx-0 mr-3 h-40px px-4" @click="cancel">
            {{ $t("authorizeAgent.cancel") }}
          </b-button>
          <b-button variant="primary" class="mx-0 h-40px px-4" :disabled="!companyId || submitting"
            @click="approve">
            {{ $t("authorizeAgent.authorize") }}
          </b-button>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";

// The app's own query keys, which the authorization request knows nothing about.
const APP_QUERY_KEYS = ["lang", "geotabAddin"];

export default {
  name: "AuthorizeAgent",
  data() {
    return {
      companyId: null,
      // Held while the approval is in flight, so Authorize cannot be pressed
      // twice; the card stays as it is until the browser leaves for the agent.
      submitting: false,
      error: "",
      // A request that cannot be approved at all, as opposed to one that failed.
      fatalError: "",
    };
  },
  computed: {
    ...mapGetters(["userCompanies"]),
    // Only a verified company can be connected; an unverified one has nothing
    // to give access to yet.
    companyOptions() {
      return (this.userCompanies || [])
        .filter((company) => company.is_verified)
        .map((company) => ({ value: company.id, text: company.name }));
    },
  },
  methods: {
    ...mapActions(["approveMcpAgent", "getUserCompanies", "getUserCompany"]),
    // Everything the request arrived with goes back up as it stands, so a
    // parameter added to the handshake later needs no change here.
    requestPayload() {
      const query = this.$route.query || {};
      return Object.keys(query).reduce((payload, key) => {
        if (APP_QUERY_KEYS.includes(key)) return payload;
        const value = query[key];
        payload[key] = Array.isArray(value) ? value[0] : value;
        return payload;
      }, {});
    },
    async approve() {
      this.error = "";
      const payload = this.requestPayload();
      if (!payload.client_id || !payload.redirect_uri) {
        this.fatalError = this.$t("authorizeAgent.badRequest");
        return;
      }
      this.submitting = true;
      try {
        // The company that was picked is the one the request is made for, and
        // the one the app itself works in from here on.
        localStorage.setItem("userSelectedCompany", this.companyId);
        this.getUserCompany();
        const data = await this.approveMcpAgent({ ...payload, company_id: this.companyId });
        const redirect = data && data.redirect_to;
        if (!redirect) {
          this.error = this.$t("authorizeAgent.failed");
          return;
        }
        // It leaves the app entirely, so this is a location change rather than
        // a route push.
        window.location.href = redirect;
      } catch (error) {
        this.error = this.apiError(error);
      } finally {
        this.submitting = false;
      }
    },
    cancel() {
      this.$router.push("/");
    },
    apiError(error) {
      if (typeof error === "string") return error;
      if (error && error.error) return Array.isArray(error.error) ? error.error[0] : error.error;
      if (error && error.detail) return error.detail.error || error.detail;
      const first = error && Object.values(error)[0];
      if (Array.isArray(first)) return first[0];
      return first || this.$t("authorizeAgent.failed");
    },
  },
  async created() {
    const payload = this.requestPayload();
    if (!payload.client_id || !payload.redirect_uri) {
      this.fatalError = this.$t("authorizeAgent.badRequest");
      return;
    }
    try {
      await this.getUserCompanies();
    } catch (error) {
      this.error = this.apiError(error);
    }
    // The company already in use is the one offered first.
    const current = localStorage.getItem("userSelectedCompany");
    const options = this.companyOptions;
    const match = options.find((option) => `${option.value}` === `${current}`);
    this.companyId = match ? match.value : (options[0] && options[0].value) || null;
    if (!options.length) this.fatalError = this.$t("authorizeAgent.noCompanies");
  },
};
</script>
<style scoped>
/* Nothing of the app shows through: no sidebar, no header, just the card. */
.authorize-agent {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #f8f9fb;
  overflow-y: auto;
}

.authorize-card {
  width: 100%;
  max-width: 520px;
  padding: 40px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(17, 31, 94, 0.06);
  text-align: center;
}

.authorize-title {
  font-size: 22px;
  font-weight: 600;
  color: #212529;
}

.authorize-note {
  font-size: 14px;
  color: #6c757d;
}

.authorize-error {
  font-size: 13px;
  color: #ff0000;
}
</style>
